import { useId, useState } from 'react';
import { Button, ErrorState, FormField, Modal, TextField } from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatCents } from '../../lib/format/money';
import { notify } from '../../services/notify';
import type { EffectiveLever, LeverValue } from './leversApi';
import { usePatchLocaleLevers } from './leversQueries';

/** The max price the dialog starts with, in dollars. */
const DEFAULT_MAX_PRICE = '12';

/**
 * Levers Turn on pricing also sets, but ONLY where the locale has no value of
 * its own: every priced locale then carries them explicitly, even when equal
 * to the default, so locales read the same in the levers. A value the locale
 * already has is never changed.
 */
const SET_IF_UNSET: Array<{ key: string; value: LeverValue }> = [
  { key: 'bufferFeePercentage', value: 0 },
  { key: 'maxShareConnectionLevel', value: 2 },
];

/** The SET_IF_UNSET levers this locale has no value of its own for. */
function unsetDefaults(levers: EffectiveLever[]) {
  return SET_IF_UNSET.filter((d) => levers.find((l) => l.key === d.key)?.override === null);
}

/**
 * "Turn on pricing" on a locale whose max price is $0 (pricing off: nothing in
 * it can be priced). One step: sets the locale's own max price. The caller
 * shows it only while pricing is off; without levers.write it is disabled, not
 * hidden.
 *
 * It also sets the buffer fee (0%) and max share connection level (2) where
 * the locale has none of its own (SET_IF_UNSET), in the same change, without
 * mentioning it: the admin is just turning pricing on.
 *
 * Max price is a money lever, and the BE refuses a money lever change without
 * a reason for the audit log. The dialog has no reason field, so the change
 * carries one saying what was done.
 */
export function TurnOnPricing({
  localeId,
  localeName,
  levers,
}: {
  localeId: string;
  localeName: string;
  levers: EffectiveLever[];
}) {
  const { locked } = useSession();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost-dark" size="sm" lockedReason={locked('levers.write')} onClick={() => setOpen(true)}>
        Turn on pricing
      </Button>
      {open && (
        <TurnOnPricingDialog
          localeId={localeId}
          localeName={localeName}
          alsoSet={unsetDefaults(levers)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function TurnOnPricingDialog({
  localeId,
  localeName,
  alsoSet,
  onClose,
}: {
  localeId: string;
  localeName: string;
  alsoSet: typeof SET_IF_UNSET;
  onClose: () => void;
}) {
  const fieldId = useId();
  const patch = usePatchLocaleLevers(localeId);
  const [raw, setRaw] = useState(DEFAULT_MAX_PRICE);

  const dollars = Number(raw);
  const cents = Math.round(dollars * 100);
  const valid = raw.trim() !== '' && Number.isFinite(dollars) && cents > 0;

  const turnOn = () =>
    patch.mutate(
      {
        changes: { maxPriceCents: cents, ...Object.fromEntries(alsoSet.map((d) => [d.key, d.value])) },
        reason: `Turned on pricing: max price ${formatCents(cents)}`,
      },
      {
        onSuccess: () => {
          notify.success(`Pricing is on in ${localeName}.`);
          onClose();
        },
      },
    );

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title="Turn on pricing"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose} disabled={patch.isPending}>
            Cancel
          </Button>
          <Button size="sm" disabled={!valid} loading={patch.isPending} onClick={turnOn}>
            Turn on
          </Button>
        </>
      }
    >
      <p className="mb-3.5 mt-0 text-sm text-muted">
        Sets the max price for {localeName}. Pricing is off while it is $0.
      </p>
      <FormField
        label="Max price"
        htmlFor={fieldId}
        required
        error={raw.trim() !== '' && !valid ? 'Enter a price above $0.' : undefined}
      >
        <TextField
          id={fieldId}
          surface="white"
          inputMode="decimal"
          leading="$"
          value={raw}
          invalid={raw.trim() !== '' && !valid}
          onChange={(e) => setRaw(e.target.value)}
        />
      </FormField>
      {patch.error && <ErrorState error={patch.error} compact />}
    </Modal>
  );
}
